import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service';
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model';
import { IAllowedInputDates } from '../../../../context/report-periods/report-periods.model';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../../presentation-components/form-field-components/supplier-picker';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { IEditFileUploadLineSpendBased, IFileUploadLineSpendBased } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';
import GroupService from '../../../../shared/services/group/group.service';
import { IGroup } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { EmissionsInputAdvanced } from '../../../presentation-components/emissons-components/overview/emissions-input/emissions-input-advanced-form';
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { isEtsGroupId } from '../../../../shared/utils/group-ids-utils';
import { formatConversionCodeLabel, sortByConversionCode } from '../../../../shared/utils/conversion-utils';

export const EditUploadLineSpendBasedDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineSpendBased,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineSpendBased) => void,
  isSubmitting: boolean,
  errorCode?: number,
  errorMessage?: string,
}> = ({
  editUploadLine,
  open,
  closeHandler,
  submitHandler,
  isSubmitting,
  errorCode,
  errorMessage,
}) => {
    //Service
    const customFieldService = new CustomFieldService()
    const subsidiaryService = SubsidiaryService.getInstance()
    const conversionSourceService = new ConversionSourceService()
    const spendBasedConversionService = new SpendBasedConversionService()
    const groupService = new GroupService()
    const contactSupplierService = ContactSupplierService.getInstance();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    const organisationDetails = userDetailsContext.userDetails?.organisation
    const reportPeriodContext = useContext(ReportPeriodContext)
    //State
    const [groups, setGroups] = useState<IGroup[]>()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>()
    const sortedSpendBasedConversions = useMemo(
      () => sortByConversionCode(spendBasedConversions),
      [spendBasedConversions],
    )
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
    const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>()
    const [openDate, setOpenDate] = useState(false);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [allowedDates, setAllowedDates] = useState<IAllowedInputDates>({ startDate: undefined, endDate: undefined })

    //Use Effects
    useEffect(() => {
      if (editUploadLine.reportPeriodId && reportPeriodContext.allReportPeriods) {
        const reportPeriod = reportPeriodContext.allReportPeriods.find(rp => rp.id === editUploadLine.reportPeriodId)
        if (reportPeriod) {
          setAllowedDates({
            startDate: reportPeriod.startDate,
            endDate: reportPeriod.endDate,
          })
        }
      }
    }, [editUploadLine.reportPeriodId, reportPeriodContext.allReportPeriods])

    useEffect(() => {
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))

      groupService.getGroups()
        .then(response => {
          setGroups(response)
        })
        .catch(err => console.log(err))

      conversionSourceService.getAll(true, 'spendBasedConversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))
    }, [])

    useEffect(() => {
      if (conversionSources && allowedDates) {
        subsidiaryService.getAll()
          .then(response => {
            setSubsidiaries(response)
          })
          .catch(err => console.log(err))

        const year = moment(editUploadLine.invoiceDate).format('YYYY')

        contactSupplierService.getAllActiveSuppliersWithUpdatedSpendBasedConversions(year)
          .then(response => {
            setContactSuppliers(response)
          })
          .catch(err => console.log(err))
      }
    }, [conversionSources])

    useEffect(() => {
      if (customFields && customFields.length && editUploadLine?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = editUploadLine.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? '';
          formik.setFieldValue(field.id, matchingValue, false);
        });
      }
    }, [customFields, editUploadLine]);

    useEffect(() => {
      if (conversionSources && allowedDates) {
        let sourceId = ''
        // Priority 1: Selected in spreadsheet
        if (editUploadLine.conversionSourceId) {
          sourceId = editUploadLine.conversionSourceId
        }
        // Priority 2: Selected by supplier
        else if (editUploadLine.contactSupplierId && contactSuppliers) {
          const supplier = contactSuppliers.find(s => s.id === editUploadLine.contactSupplierId)
          if (supplier?.defaultSpendBasedConversionSourceId) {
            sourceId = supplier.defaultSpendBasedConversionSourceId
          }
        }
        // Priority 3: Selected by subsidiary
        else if (editUploadLine.subsidiaryId && subsidiaries) {
          const subsidiary = subsidiaries.find(s => s.id === editUploadLine.subsidiaryId)
          if (subsidiary?.defaultSpendBasedConversionSourceId) {
            sourceId = subsidiary.defaultSpendBasedConversionSourceId
          }
        }
        // Priority 4: User's preferred subsidiary default
        else if (subsidiaries && userDetails) {
          const preferred = getPreferredSubsidiary(subsidiaries, userDetails)
          if (preferred?.defaultSpendBasedConversionSourceId) {
            sourceId = preferred.defaultSpendBasedConversionSourceId
          }
        }
        if (sourceId && conversionSources.find(cs => cs.id === sourceId)) {
          // Set the conversion source in formik if it's not already set
          if (sourceId !== formik.values.conversionSourceId) {
            formik.setFieldValue('conversionSourceId', sourceId, false)
          }
          const year = moment(editUploadLine.invoiceDate).format('YYYY')
          spendBasedConversionService.getSpendBasedConversionsForConversionSource(sourceId, year, true, true)
            .then(response => {
              setSpendBasedConversions(response)
              if (editUploadLine.spendBasedConversionId && response) {
                const existsInReturns = response.some(r => r.id === editUploadLine.spendBasedConversionId)
                if (existsInReturns) {
                  formik.setFieldValue('returnId', editUploadLine.spendBasedConversionId, false)
                }
              }
            })
            .catch(err => console.log(err))
        }
      }
    }, [conversionSources, allowedDates, subsidiaries, contactSuppliers])

    const editUploadSchema = Yup.object().shape({
      invoiceNumber: Yup.string().nullable(),
      contactSupplierId: Yup.string().nullable(),
      contactName: Yup.string().required(`${screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} is required`),
      returnId: Yup.string().required('You must select an activity'),
      total: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      invoiceDate: Yup.string().required('You must select a date'),
      startDate: Yup.string().nullable()
        .test('start-end-pair', 'Provide both a start and end date, or neither', function (value) {
          return !!value === !!this.parent.endDate
        }),
      endDate: Yup.string().nullable()
        .test('start-end-pair', 'Provide both a start and end date, or neither', function (value) {
          return !!value === !!this.parent.startDate
        })
        .test('after-start', 'End date must be on or after the start date', function (value) {
          if (!value || !this.parent.startDate) return true
          return !moment(value).isBefore(moment(this.parent.startDate), 'day')
        }),
      reference: Yup.string().nullable(),
      bankAccountName: Yup.string().nullable(),
      groupId: Yup.string().required('You must select a group'),
      conversionSourceId: Yup.string().required('Emission Factor Source is required'),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      countryId: Yup.string().required('Country is required'),
      projectId: Yup.string().nullable(),
      locationId: Yup.string().nullable(),
      vehicleId: Yup.string().nullable(),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeId: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      })
    })

    const formik = useFormik({
      initialValues: {
        groupId: editUploadLine.groupId || '',
        countryId: editUploadLine.countryId || '',
        total: editUploadLine.total || undefined,
        invoiceDate: editUploadLine.invoiceDate || '',
        startDate: editUploadLine.startDate || '',
        endDate: editUploadLine.endDate || '',
        invoiceNumber: editUploadLine.invoiceNumber || '',
        reference: editUploadLine.reference || '',
        bankAccountName: editUploadLine.bankAccountName || '',
        contactSupplierId: editUploadLine.contactSupplierId || '',
        contactName: editUploadLine.contactName || '',
        returnId: editUploadLine.spendBasedConversionId || '',
        conversionSourceId: '',
        subsidiaryId: editUploadLine.subsidiaryId || '',
        projectId: editUploadLine.projectId || '',
        locationId: editUploadLine.locationId || '',
        vehicleId: editUploadLine.vehicleId || '',
        partOfEts: editUploadLine.regulatedEmissionTradingSchemeName ? true : false,
        regulatedEmissionTradingSchemeId: editUploadLine.regulatedEmissionTradingSchemeId || '',
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
      },
      validationSchema: editUploadSchema,
      enableReinitialize: true,
      onSubmit: (values) => {
        const response = formatTransaction(values, values.groupId, true, customFields)
        submitHandler(editUploadLine.id, response)
      }
    })

    const conversionSourceHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('conversionSourceId', e.target.value)
      if (e.target.value !== formik.values.conversionSourceId) {
        formik.setFieldValue('returnId', '')
        const year = moment(formik.values.invoiceDate || editUploadLine.invoiceDate).format('YYYY')
        spendBasedConversionService.getSpendBasedConversionsForConversionSource(e.target.value, year, true, true)
          .then(response => {
            setSpendBasedConversions(response)
          })
          .catch(err => console.log(err))
      }
    }

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'));
        const year = moment(date).format('YYYY');
        const prevYear = moment(formik.values.invoiceDate || editUploadLine.invoiceDate).format('YYYY');
        const yearChanged = prevYear !== year;
        if (yearChanged && formik.values.conversionSourceId) {
          spendBasedConversionService.getSpendBasedConversionsForConversionSource(formik.values.conversionSourceId, year, true, true)
            .then(response => {
              setSpendBasedConversions(response)
              const stillValid = response?.some(conversion => conversion.id === formik.values.returnId)
              if (!stillValid) {
                formik.setFieldValue('returnId', '')
              }
              return contactSupplierService.getAllActiveSuppliersWithUpdatedSpendBasedConversions(year)
                .then(updatedSuppliers => {
                  setContactSuppliers(updatedSuppliers)
                  const selectedSupplier = updatedSuppliers?.find(s => s.id === formik.values.contactSupplierId)
                  if (selectedSupplier?.spendBasedConversionId) {
                    const existsInReturns = response?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                    if (existsInReturns) {
                      formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                    }
                  }
                })
            })
            .catch(err => console.log(err))
        }
      }
    }

    // The optional consumption period: no factor-year refresh (that keys off the invoice date),
    // and a cleared picker clears the value so the line falls back to the invoice date.
    const optionalDateHandler = (date: Moment | null, name: string) => {
      formik.setFieldValue(name, date && date.isValid() ? date.format('YYYY-MM-DD') : '')
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(s => s.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
        const defaultConversionSourceId = subsidiary.defaultSpendBasedConversionSourceId
        if (conversionSources?.find(conversionSource => conversionSource.id === defaultConversionSourceId)) {
          if (formik.values.conversionSourceId !== defaultConversionSourceId) {
            formik.setFieldValue('returnId', '')
          }
          formik.setFieldValue('conversionSourceId', defaultConversionSourceId)
          const year = moment(formik.values.invoiceDate || editUploadLine.invoiceDate).format('YYYY')
          spendBasedConversionService.getSpendBasedConversionsForConversionSource(defaultConversionSourceId, year, true, true)
            .then(response => {
              setSpendBasedConversions(response)
            })
            .catch(err => console.log(err))
        }
      }
    }

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <>
        <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
          <Box sx={{ p: 4 }} component='form'>
            <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
              Edit Uploaded Data
            </DialogTitle>
            <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
              {allowedDates && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label='Date'
                    value={formik.values.invoiceDate ? moment(formik.values.invoiceDate) : null}
                    onChange={(date) => dateHandler(date, 'invoiceDate')}
                    format='DD/MM/YYYY'
                    maxDate={moment(allowedDates.endDate)}
                    minDate={moment(allowedDates.startDate)}
                    sx={{ color: 'text.primary' }}
                    open={openDate}
                    onOpen={() => setOpenDate(true)}
                    onClose={() => setOpenDate(false)}
                    slotProps={{
                      textField: {
                        onClick: () => setOpenDate(true),
                        error: formik.touched.invoiceDate && Boolean(formik.errors.invoiceDate)
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>}
              {allowedDates && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label='Start Date (optional)'
                    value={formik.values.startDate ? moment(formik.values.startDate) : null}
                    onChange={(date) => optionalDateHandler(date, 'startDate')}
                    format='DD/MM/YYYY'
                    maxDate={moment(allowedDates.endDate)}
                    minDate={moment(allowedDates.startDate)}
                    sx={{ color: 'text.primary' }}
                    open={openStartDate}
                    onOpen={() => setOpenStartDate(true)}
                    onClose={() => setOpenStartDate(false)}
                    slotProps={{
                      field: { clearable: true },
                      textField: {
                        onClick: () => setOpenStartDate(true),
                        error: formik.touched.startDate && Boolean(formik.errors.startDate)
                      },
                    }}
                  />
                </LocalizationProvider>
                {formik.touched.startDate && formik.errors.startDate &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    <div>{formik.errors.startDate as string}</div>
                  </FormHelperText>
                }
              </FormControl>}
              {allowedDates && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label='End Date (optional)'
                    value={formik.values.endDate ? moment(formik.values.endDate) : null}
                    onChange={(date) => optionalDateHandler(date, 'endDate')}
                    format='DD/MM/YYYY'
                    maxDate={moment(allowedDates.endDate)}
                    minDate={formik.values.startDate ? moment(formik.values.startDate) : moment(allowedDates.startDate)}
                    sx={{ color: 'text.primary' }}
                    open={openEndDate}
                    onOpen={() => setOpenEndDate(true)}
                    onClose={() => setOpenEndDate(false)}
                    slotProps={{
                      field: { clearable: true },
                      textField: {
                        onClick: () => setOpenEndDate(true),
                        error: formik.touched.endDate && Boolean(formik.errors.endDate)
                      },
                    }}
                  />
                </LocalizationProvider>
                {formik.touched.endDate && formik.errors.endDate &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    <div>{formik.errors.endDate as string}</div>
                  </FormHelperText>
                }
              </FormControl>}
              {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
                  <Select
                    id='subsidiaryId' label='Subsidiary' name='subsidiaryId'
                    value={formik.values.subsidiaryId || ''}
                    onChange={subsidiaryHandler}
                    error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                    sx={{ color: 'text.primary', }}>
                    {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                      <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>}
              {contactSuppliers &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <SupplierPicker
                    id="contactName"
                    suppliers={contactSuppliers}
                    freeSolo
                    value={formik.values.contactSupplierId}
                    inputValue={formik.values.contactName}
                    error={formik.touched.contactName && Boolean(formik.errors.contactName)}
                    helperText={formik.touched.contactName && formik.errors.contactName ? formik.errors.contactName as string : null}
                    onChange={(_id, newValue, freeText) => {
                      const selectedSupplier = newValue || undefined;
                      formik.setFieldValue('contactSupplierId', newValue?.id || '');
                      formik.setFieldValue('contactName', freeText ?? (newValue?.name || ''));
                      if (selectedSupplier?.defaultSpendBasedConversionSourceId) {
                        const defaultId = selectedSupplier.defaultSpendBasedConversionSourceId;
                        const existsInSources = conversionSources?.some(cs => cs.id === defaultId);
                        if (existsInSources) {
                          if (formik.values.conversionSourceId !== defaultId) {
                            formik.setFieldValue('returnId', '');
                            formik.setFieldValue('conversionSourceId', defaultId);
                            const year = moment(formik.values.invoiceDate || editUploadLine.invoiceDate).format('YYYY')
                            spendBasedConversionService
                              .getSpendBasedConversionsForConversionSource(
                                defaultId,
                                year,
                                true,
                                true
                              )
                              .then(response => {
                                setSpendBasedConversions(response)
                                if (selectedSupplier?.spendBasedConversionId) {
                                  const existsInReturns = response?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                                  if (existsInReturns) {
                                    formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                                  }
                                }
                              })
                              .catch(err => console.log(err))
                          } else {
                            // Source unchanged: do not refetch conversions; set activity if valid in current list
                            if (selectedSupplier?.spendBasedConversionId) {
                              const existsInReturns = spendBasedConversions?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                              if (existsInReturns) {
                                formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                              }
                            }
                          }
                        }
                      }
                      // If no default source change, still set activity if available and valid for current list
                      if (!selectedSupplier?.defaultSpendBasedConversionSourceId && selectedSupplier?.spendBasedConversionId) {
                        const existsInReturns = spendBasedConversions?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                        if (existsInReturns) {
                          formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                        }
                      }
                    }}
                    onInputChange={(newInputValue) => {
                      formik.setFieldValue('contactName', newInputValue);
                      // If user types arbitrary text, clear the UUID
                      if (formik.values.contactSupplierId && !contactSuppliers?.find(s => s.name === newInputValue)) {
                        formik.setFieldValue('contactSupplierId', '');
                      }
                    }}
                  />
                </FormControl>}
              {groups &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='groupId'>Group</InputLabel>
                  <Select
                    id='groupId' label='Group' name='groupId'
                    value={formik.values.groupId}
                    onChange={formik.handleChange}
                    error={formik.touched.groupId && Boolean(formik.errors.groupId)}
                    sx={{ color: 'text.primary', }}>
                    {sortGroupsByGhgCategory(groups).map((group, index) =>
                      <MenuItem key={index} value={group.id}>{formatGroupLabel(group)}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.groupId && formik.errors.groupId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>}
              {conversionSources &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='conversionSourceId'>Conversion Source</InputLabel>
                  <Select
                    id='conversionSourceId' label='Conversion Source' name='conversionSourceId'
                    value={formik.values.conversionSourceId}
                    onChange={conversionSourceHandler}
                    error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
                    sx={{ color: 'text.primary', }}>
                    {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((source, index) =>
                      <MenuItem key={index} value={source.id}>{source.source}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.conversionSourceId ? <div>{formik.errors.conversionSourceId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>}
              <FormControl fullWidth variant='outlined' sx={{ flexBasis: '200px', mt: 2 }}>
                <Autocomplete
                  id="returnId"
                  options={sortedSpendBasedConversions}
                  freeSolo={false}
                  autoHighlight
                  disabled={!spendBasedConversions || spendBasedConversions.length === 0}
                  getOptionLabel={(option) => typeof option === 'string' ? option : formatConversionCodeLabel(option)}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box
                        component="li"
                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                        key={key}
                        {...restProps}
                      >
                        {formatConversionCodeLabel(option, ' -')}
                      </Box>
                    )
                  }}
                  value={spendBasedConversions?.find(conversion => conversion.id === formik.values.returnId) || null}
                  onChange={(event, newValue) => {
                    formik.setFieldValue('returnId', typeof newValue === 'string' ? '' : newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ color: 'text.primary' }}
                      label="Activity"
                      error={formik.touched.returnId && Boolean(formik.errors.returnId)}
                      helperText={formik.touched.returnId && formik.errors.returnId ? formik.errors.returnId as string : null}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='total'>Amount in {organisationDetails?.currencyCode || 'GBP'} (including tax)</InputLabel>
                <OutlinedInput inputProps={{ min: 0 }} id='total' label={`Amount in ${organisationDetails?.currencyCode || 'GBP'} (including tax)`} name='total' type='number'
                  value={formik.values.total}
                  onChange={formik.handleChange}
                  error={formik.touched.total && Boolean(formik.errors.total)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.total && formik.errors.total &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.total}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='invoiceNumber'>Invoice Number</InputLabel>
                <OutlinedInput id='invoiceNumber' label='Invoice Number' name='invoiceNumber' type='text'
                  value={formik.values.invoiceNumber}
                  onChange={formik.handleChange}
                  error={formik.touched.invoiceNumber && Boolean(formik.errors.invoiceNumber)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.invoiceNumber && formik.errors.invoiceNumber &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.invoiceNumber}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='reference'>Reference</InputLabel>
                <OutlinedInput id='reference' label='Reference' name='reference' type='text'
                  value={formik.values.reference}
                  onChange={formik.handleChange}
                  error={formik.touched.reference && Boolean(formik.errors.reference)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.reference && formik.errors.reference &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.reference}</FormHelperText>
                }
              </FormControl>
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='bankAccountName'>Bank Account</InputLabel>
                <OutlinedInput id='bankAccountName' label='Bank Account' name='bankAccountName' type='text'
                  value={formik.values.bankAccountName}
                  onChange={formik.handleChange}
                  error={formik.touched.bankAccountName && Boolean(formik.errors.bankAccountName)}
                  sx={{ color: 'text.primary' }}></OutlinedInput>
                {formik.touched.bankAccountName && formik.errors.bankAccountName &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.bankAccountName}</FormHelperText>
                }
              </FormControl>
              {customFields &&
                <EmissionsInputAdvanced
                  customFields={customFields}
                  formik={formik}
                  hideRegulatedEmissionTradingScheme={!isEtsGroupId(formik.values.groupId)}
                  hideCustomFields={false}
                  hideSupplier={true}
                  hideRec={true}
                  hideCustomBasedMarketMultiplier={true}
                  hideEnergySupplierTariff={true}
                  hideExcludedFromReporting={true}
                />}
            </DialogContent >
            <DialogActions sx={{ p: 0 }}>
              <DialogSaveButtons
                cancelHandler={cancelHandler}
                submitHandler={formik.submitForm}
                isSubmitting={isSubmitting}
              />
            </DialogActions>
          </Box >
        </Dialog >
      </>
    )
  }
